import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { LogoutButton } from "../../buttons/logout-button";

interface NavBarMenuLink {
  path: string;
  label: string;
}

const MENU_LINKS: NavBarMenuLink[] = [
  { path: "/freebie-downloadables", label: "Freebie downloadables" },
  { path: "/pricing", label: "Pricing" },
  { path: "/about", label: "About us" },
];

/**
 * Corner menu holding the general-audience pages plus Log Out, shown only once
 * a user is signed in. Signed in, the bar itself is theirs — Dashboard and My
 * documents — and the rest stays one click away without competing for the same
 * row.
 */
export const NavBarMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="nav-bar__menu" ref={menuRef}>
      <button
        type="button"
        className="nav-bar__menu-toggle"
        aria-label="Menu"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </svg>
      </button>

      {open && (
        <div className="nav-bar__menu-dropdown" role="menu">
          {MENU_LINKS.map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              end
              role="menuitem"
              className={({ isActive }) =>
                "nav-bar__menu-item " +
                (isActive ? "nav-bar__menu-item--active" : "")
              }
              onClick={() => setOpen(false)}
            >
              {label}
            </NavLink>
          ))}

          <div className="nav-bar__menu-divider" role="separator" />
          <LogoutButton
            className="nav-bar__menu-item nav-bar__menu-item--logout"
            role="menuitem"
          />
        </div>
      )}
    </div>
  );
};
